import React from "react";
import {
    Box,
    Container,
    Grid,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";

// Icons for the cards and lists
import VerticalShadesClosedOutlinedIcon from '@mui/icons-material/VerticalShadesClosedOutlined';
import ThermostatOutlinedIcon from '@mui/icons-material/ThermostatOutlined';
import RoofingOutlinedIcon from '@mui/icons-material/RoofingOutlined';
import SolarPowerOutlinedIcon from '@mui/icons-material/SolarPowerOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

type ServiceListProps = {
    title: string;
    items: string[];
};

const services = [
    {
        title: "External Wall Insulation",
        fullTitle: "External Wall Insulation (EWI)",
        icon: <VerticalShadesClosedOutlinedIcon sx={{ fontSize: 60 }} />,
        bgColor: "#003333",
        textColor: "#fff",
        eco4Fit: [
            "BBA/Kiwa-approved system installation",
            "PAS 2035-trained, TrustMark-certified operatives",
            "Satisfies Solid Wall Minimum Requirement",
        ],
        offering: [
            "Crews for scaffolding, insulation, render, QA",
            "Site oversight by Retrofit Coordinators",
            "Documentation support: EPC uplift, RdSAP, TrustMark",
        ],
    },
    {
        title: "Cavity Wall Insulation",
        fullTitle: "Cavity Wall Insulation (CWI)",
        icon: <ThermostatOutlinedIcon sx={{ fontSize: 60 }} />,
        bgColor: "#ffffff",
        textColor: "#003333",
        eco4Fit: [
            "Certified CWI products with long warranties",
            "Rope access crews available for scaffold-free installs",
        ],
        offering: [
            "Installers for drilling, injection, sealing, airflow checks",
            "Supervisors maintain ECO logs, compliance records",
        ],
    },
    {
        title: "Loft Insulation",
        fullTitle: "Loft Insulation",
        icon: <RoofingOutlinedIcon sx={{ fontSize: 60 }} />,
        bgColor: "#003333",
        textColor: "#ffffff",
        eco4Fit: [
            "Ventilation, joist clearance, hatch sealing included",
            "Recognised standard ECO4 measure",
        ],
        offering: [
            "Depth surveys, safety boarding, hatch sealing",
            "Full PAS 2035 install and documentation trail",
        ],
    },
    {
        title: "Solar PV and Air Source Heat Pumps",
        fullTitle: "Solar PV and Air Source Heat Pumps",
        icon: <SolarPowerOutlinedIcon sx={{ fontSize: 60 }} />,
        bgColor: "#ffffff",
        textColor: "#003333",
        eco4Fit: [
            "Retrofit design, heat-loss calculations, airtightness strategy",
            "MCS paperwork, TrustMark, EPC, Ofgem documentation",
            "Innovation uplift optimisation (e.g., 45% on Grant Aerona ASHPs)",
        ],
        offering: [
            "Full design + paperwork bundles",
            "Audit packs tailored for supplier/LA Flex submission",
            "Post-install support and submission management",
        ],
    },
];

// Helper component for rendering lists to keep the main component clean
const ServiceList = ({ title, items }: ServiceListProps) => (
    <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {title}
        </Typography>
        <List dense>
            {items.map((item, index) => (
                <ListItem key={index} disableGutters>
                    <ListItemIcon sx={{ minWidth: '32px', color: '#4CAF50' }}>
                        <CheckCircleOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                </ListItem>
            ))}
        </List>
    </Box>
);


const FeaturedServices = () => {
    return (
        <Box>
            {/* SECTION 1: Summary Cards */}
            <Box sx={{ py: 8, backgroundColor: "#003333", mb: 2 }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: "bold",
                            color: "#fff",
                            mb: 5,
                        }}
                    >
                        Services we offer
                    </Typography>
                    <Grid container spacing={4}>
                        {services.map((service, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        textAlign: "center",
                                        borderRadius: "12px",
                                        backgroundColor: "#fff",
                                        p: 4,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "220px",
                                        color: "#4CAF50",
                                    }}
                                >
                                    {service.icon}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: "bold",
                                            mt: 2,
                                            color: 'black',
                                            fontSize: '1rem',
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {service.title}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* SECTION 2: Detailed Service Breakdowns */}
            {services.map((service, index) => (
                <Box
                    key={index}
                    sx={{
                        backgroundColor: service.bgColor,
                        color: service.textColor,
                        py: 6,
                        borderBottom: '1px solid #e0e0e0' // Adds a subtle separator line
                    }}
                >
                    <Container maxWidth="lg">
                        <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 4 }}>
                            {service.fullTitle}
                        </Typography>
                        <Grid container spacing={5}>
                            <Grid item xs={12} md={6}>
                                <ServiceList title="ECO4 fit:" items={service.eco4Fit} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ServiceList title="Our offering:" items={service.offering} />
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            ))}
        </Box>
    );
};

export default FeaturedServices;